import { Menu, Transition } from "@headlessui/react";
import { SVGElement } from "@ui/types";
import classNames from "classnames";
import Link from "next/link";
import React, { Fragment, ReactNode, useMemo } from "react";

export type MenuOption = {
  label: any;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: SVGElement;
  hidden?: boolean;
  selected?: boolean;
};

export interface ItemContentProps {
  active: boolean;
  option: MenuOption;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: "sm" | "lg";
}

function ItemContent({ active, option, onClick, size = "lg", ...rest }: ItemContentProps) {
  const className = classNames(
    option.selected ? "bg-muted text-foreground" : "text-foreground",
    size === "sm" ? "py-1 text-xs" : "py-2 text-sm",
    "group flex items-center px-4 w-full",
  );
  const icon = option.icon ? (
    <option.icon
      className="group-hover:text-muted-foreground w-5 h-5 mr-3 text-muted-foreground"
      aria-hidden="true"
    />
  ) : null;

  const label =
    typeof option.label === "function" ? option.label({ active, option }) : option.label;

  if (option.href) {
    return (
      <Link href={option.href} {...rest} className={className}>
        {icon}
        {label}
      </Link>
    );
  }

  return (
    <button
      className={className}
      {...rest}
      onClick={(e) => {
        if (typeof onClick === "function") {
          onClick(e);
        }
        if (typeof option.onClick === "function") {
          option.onClick(e);
        }
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export interface DropdownButtonProps {
  button: ReactNode;
  options: Array<MenuOption | MenuOption[]>;
  onClick?: (e: any) => void;
  disabled?: boolean;
  width?: string;
  size?: "sm" | "lg" | undefined;
}

export function DropdownButton(props: DropdownButtonProps) {
  const filteredOptions = useMemo(() => {
    const results: DropdownButtonProps["options"] = [];
    props.options.forEach((option) => {
      if (Array.isArray(option)) {
        const filtered = option.filter((o) => !o.hidden);
        if (filtered.length > 0) {
          results.push(filtered);
        }
        return;
      }
      if (!option.hidden) {
        results.push(option);
      }
    });
    return results;
  }, [props.options]);

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex justify-center">{props.button}</Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className="ring-1 ring-black ring-opacity-5 focus:outline-none w-52 absolute right-0 z-50 mt-2 origin-top-right bg-background divide-y divide-border rounded-md shadow-lg"
          style={props.width ? { width: props.width } : {}}
        >
          {filteredOptions.map((option, index) => (
            <div key={`group-${index}`} className="py-1">
              {Array.isArray(option) ? (
                option.map((groupedOption, innerIndex) => (
                  <Menu.Item key={`group-${index}-option-${innerIndex}`}>
                    {({ active }) => (
                      <ItemContent active={active} option={groupedOption} size={props.size} />
                    )}
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item key={index}>
                  {({ active }) => (
                    <ItemContent active={active} option={option} size={props.size} />
                  )}
                </Menu.Item>
              )}
            </div>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default DropdownButton;
